import { useTranslation } from 'react-i18next'
import { AiChatInputRequestStatus } from '@amitable/shared/ai-chat-decisions'
import { AiChatDecisionActions } from './ai-chat-decision-actions'
import { AiChatDecisionQuestion } from './ai-chat-decision-question'
import { useAiChatDecisionForm } from './use-ai-chat-decision-form'
import type { AiChatDecisionCardProps } from './use-ai-chat-decision-form'

function AiChatDecisionCardBody(props: AiChatDecisionCardProps) {
  const { request, onAnswer, onCancel } = props
  const { t } = useTranslation('aiChat')
  const form = useAiChatDecisionForm(props)
  const finalStatus =
    request.status === AiChatInputRequestStatus.ANSWERED
      ? 'decisionAnswered'
      : 'decisionCancelled'

  const visible = request.questions
    .map((question, index) => ({ question, index }))
    .filter(({ index }) => !form.pending || index === form.steps.step)

  return (
    <form
      className="mt-3 space-y-4 rounded-lg border border-border p-3"
      aria-label={t('decisionTitle')}
      onSubmit={(event) => {
        event.preventDefault()
        if (form.pending && !form.steps.isLast) form.steps.next()
        else void form.perform(false)
      }}
    >
      <p className="text-xs font-medium text-muted-foreground" role="status">
        {t(form.pending ? 'decisionWaiting' : finalStatus)}
      </p>
      {form.pending && form.steps.total > 1 ? (
        <p className="text-xs text-muted-foreground">
          {t('decisionProgress', {
            current: form.steps.step + 1,
            total: form.steps.total,
          })}
        </p>
      ) : null}
      {visible.map(({ question, index }) => (
        <AiChatDecisionQuestion
          key={question.id}
          requestId={request.id}
          question={question}
          answer={form.answers[index]}
          disabled={form.locked}
          onChange={form.changeAnswer}
          headingRef={form.pending ? form.steps.headingRef : undefined}
        />
      ))}
      {form.error ? (
        <p role="alert" className="text-sm text-destructive">
          {form.error}
        </p>
      ) : null}
      {form.pending ? (
        <AiChatDecisionActions
          form={form}
          canAnswer={Boolean(onAnswer)}
          canCancel={Boolean(onCancel)}
        />
      ) : null}
    </form>
  )
}

export function AiChatDecisionCard(props: AiChatDecisionCardProps) {
  return <AiChatDecisionCardBody key={props.request.id} {...props} />
}
